import {
  filterMailMergeOptions, FORM_FIELD_SUPPORTED_ENTITIES, FormFieldItem, getMailMergeValue, isMailMergeUsed,
} from '@repo/crm-atoms';
import { type GetActivityAttributesProps } from '@repo/crm-atoms';
import {
  AutoComplete, Checkbox, Flex, Form, Input,
} from 'antd';
import useMailMergeFields from 'automations/hooks/useMailMergeFields';
import { useCallback, useMemo, useState } from 'react';

import styles from './AddLeadActivityMenu.module.scss';

export default function ActivityAttributeForm({
  attributeKey,
  value,
  onChange,
  activityAttributes,
}: {
  attributeKey: string;
  value: string;
  onChange: (value: string) => void;
  activityAttributes?: GetActivityAttributesProps['activityAttribute'];
}) {
  const [useMailMerge, setUseMailMerge] = useState<boolean>(isMailMergeUsed(value));

  const selectedActivityAttribute = useMemo(() => activityAttributes?.find(
    (attribute) => attribute.schemaName === attributeKey,
  ), [activityAttributes, attributeKey]);

  const {
    options: mailMergeOptions,
  } = useMailMergeFields({
    allowedDataTypes: [selectedActivityAttribute?.dataType ?? ''],
  });

  const showOptions = value.includes('@');
  const searchString = value.slice(value.lastIndexOf('@') + 1);
  const filteredOptions = filterMailMergeOptions(mailMergeOptions, searchString);

  const handleSelect = useCallback((newValue: string) => {
    onChange(value.slice(0, value.lastIndexOf('@')) + getMailMergeValue(newValue));
  }, [onChange, value]);

  const handleFormFieldItemChange = useCallback((_: any, allFields: any) => {
    if (value !== allFields[attributeKey]) {
      onChange(allFields[attributeKey]);
    }
  }, [value, attributeKey, onChange]);

  return (
    <Flex gap={8}>
      {useMailMerge ? (
        <AutoComplete
          options={showOptions ? filteredOptions : []}
          value={value}
          onChange={(val) => onChange(val)}
          className={styles.valueInput}
          onSelect={handleSelect}
        >
          <Input
            placeholder="Enter Attribute Value, use @ for mail merge options"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </AutoComplete>
      ) : (
        <Form onValuesChange={handleFormFieldItemChange}>
          <FormFieldItem
            mode="edit"
            displayName=""
            formItemProps={{
              noStyle: true,
            }}
            value={value}
            dataType={selectedActivityAttribute?.dataType ?? ''}
            placeholder={selectedActivityAttribute?.attributeName ?? ''}
            schemaName={attributeKey}
            dataTypeConfig={selectedActivityAttribute?.dataTypeConfig}
            entityId={Number(selectedActivityAttribute?.id)}
            entityName={FORM_FIELD_SUPPORTED_ENTITIES.activityAttribute}
            dropdownInputProps={{
              className: styles.select,
            }}
            ownerInputProps={{
              className: styles.select,
            }}
          />
        </Form>
      )}
      <Checkbox
        className={styles.checkbox}
        checked={useMailMerge}
        onChange={(e) => setUseMailMerge(e.target.checked)}
      >
        Use Mail Merge
      </Checkbox>
    </Flex>
  );
}
